import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type RailSection = { id: string; label: string };

type Props = {
  sections: RailSection[];
};

/**
 * Bookmark-style rail fixed to center-right.
 * - Default: slim, transparent column of tick marks.
 * - On hover/focus of a single tick: a small floating chip with the label
 *   pops out to the left of that tick (no whole-rail expansion).
 * - Active section is tracked via IntersectionObserver and highlighted.
 * - Click smooth-scrolls to the section and updates the URL hash.
 */
export function SectionRail({ sections }: Props) {
  const [activeId, setActiveId] = useState<string | null>(sections[0]?.id ?? null);

  useEffect(() => {
    if (sections.length < 2) return;
    const elements = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -50% 0px", threshold: [0, 0.25, 0.5] },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  function jumpTo(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  }

  if (sections.length < 2) return null;

  return (
    <nav
      aria-label="Section navigation"
      className="pointer-events-none fixed right-3 top-1/2 z-40 hidden -translate-y-1/2 md:block"
    >
      <ul role="list" className="group/rail flex flex-col items-end gap-3">
        {sections.map((s) => {
          const isActive = s.id === activeId;
          return (
            <li key={s.id} className="group relative pointer-events-auto">
              <button
                type="button"
                onClick={() => jumpTo(s.id)}
                aria-label={`Jump to ${s.label}`}
                aria-current={isActive ? "true" : undefined}
                className="flex h-5 items-center rounded-sm px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              >
                {/* Mobile / tablet: dot */}
                <span
                  aria-hidden="true"
                  className={cn(
                    "block rounded-full transition-all duration-200 md:hidden",
                    isActive
                      ? "h-2.5 w-2.5 bg-accent"
                      : "h-1.5 w-1.5 bg-primary/40 group-hover:h-2 group-hover:w-2 group-hover:bg-primary/70",
                  )}
                />
                {/* Desktop: line */}
                <span
                  aria-hidden="true"
                  className={cn(
                    "hidden h-[2px] rounded-full transition-all duration-200 md:block",
                    isActive
                      ? "w-7 bg-accent"
                      : "w-4 bg-primary/30 group-hover:w-6 group-hover:bg-primary/70",
                  )}
                />
              </button>
              <span
                aria-hidden="true"
                className={cn(
                  "pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 translate-x-1 whitespace-nowrap rounded-md bg-white px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary opacity-0 shadow-md ring-1 ring-border/60 transition-all duration-150 group-hover/rail:translate-x-0 group-hover/rail:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100",
                  isActive && "text-gold",
                )}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
